import {toast} from "react-toastify"

let reAuthHandler = null
let reAuthInProgress = false

const REAUTH_MIN_INTERVAL_MS = 15000
const LAST_REAUTH_KEY = "tom:lastReAuth"

const readLastReAuth = () => {
    try {
        return Number(sessionStorage.getItem(LAST_REAUTH_KEY)) || 0
    } catch {
        return 0
    }
}

const writeLastReAuth = (timestamp) => {
    try {
        sessionStorage.setItem(LAST_REAUTH_KEY, String(timestamp))
    } catch {
    }
}

const notifyReAuthLoop = () =>
    toast.error(
        "Anmeldung wiederholt fehlgeschlagen. Bitte die Seite neu laden oder einen Administrator kontaktieren.",
        {toastId: "reauth-loop"},
    )

const triggerReAuth = () => {
    if (!reAuthHandler || reAuthInProgress) return
    if (Date.now() - readLastReAuth() < REAUTH_MIN_INTERVAL_MS) {
        notifyReAuthLoop()
        return
    }
    reAuthInProgress = true
    writeLastReAuth(Date.now())
    Promise.resolve(reAuthHandler()).finally(() => {
        reAuthInProgress = false
    })
}

const notifyNoRole = () =>
    toast.error("Dieser Benutzer hat keine Rolle. Bitte einen Administrator kontaktieren.", {
        toastId: "no-role",
    })

const handleForbidden = (response) =>
    parseJsonSafely(response).then((payload) => {
        if (payload?.code === "NO_ROLE") notifyNoRole()
        return Promise.reject(payload)
    })

const enrichBaseUrl = () => globalThis._env_.TOM_SERVER_URL + "/api/v1/enrich"

const parseJsonSafely = (response) =>
    response
        .json()
        .catch(() => ({error: `HTTP error! status: ${response.status}`}))

const handleResponse = (response) => {
    if (response.status === 401) {
        triggerReAuth()
        return parseJsonSafely(response).then((payload) => Promise.reject(payload))
    }
    if (response.status === 403) return handleForbidden(response)
    if (response.status === 204) return Promise.resolve({})
    if (response.ok) return response.json()
    return parseJsonSafely(response).then((payload) => Promise.reject(payload))
}

const backendResponseHandlers = {
    202: () => Promise.resolve(),
    204: () => Promise.resolve(),
    500: () => {
        toast.error("kritischer Fehler", {toastId: "calc-error"})
        return Promise.resolve()
    },
    401: (response) => {
        triggerReAuth()
        return parseJsonSafely(response).then((payload) => Promise.reject(payload))
    },
    403: (response) => handleForbidden(response),
}

const handleBackendResponse = (response) => {
    const handler = backendResponseHandlers[response.status]
    if (handler) return handler(response)
    if (response.ok) return response.json()
    return parseJsonSafely(response).then((payload) => Promise.reject(payload))
}

const RETRY_ATTEMPTS = 3
const RETRY_BASE_DELAY_MS = 500
const RETRY_MAX_DELAY_MS = 2000
const GATEWAY_STATUSES = [502, 503, 504]

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const isNetworkError = (error) => error instanceof TypeError

const fetchWithRetry = (url, options, retryStatuses = []) => {
    const nextDelay = (waitMs) => Math.min(waitMs * 2, RETRY_MAX_DELAY_MS)
    const run = (remaining, waitMs) =>
        fetch(url, options).then(
            (response) => {
                if (remaining > 1 && retryStatuses.includes(response.status)) {
                    return delay(waitMs).then(() => run(remaining - 1, nextDelay(waitMs)))
                }
                return response
            },
            (error) => {
                if (remaining > 1 && isNetworkError(error)) {
                    return delay(waitMs).then(() => run(remaining - 1, nextDelay(waitMs)))
                }
                throw error
            },
        )
    return run(RETRY_ATTEMPTS, RETRY_BASE_DELAY_MS)
}

const TomClientGen = () => {
    let token = ""

    const setToken = (newToken) => {
        token = newToken
    }

    const setReAuthHandler = (authHandler) => {
        reAuthHandler = authHandler
    }

    const request = (path, {method, body, query} = {}) => {
        const url = new URL(enrichBaseUrl() + path)
        if (query) {
            for (const [key, value] of Object.entries(query)) {
                if (value !== null && value !== undefined) url.searchParams.append(key, value)
            }
        }
        const options = {
            method: method ?? "GET",
            headers: {Authorization: `Bearer ${token}`},
        }
        if (body !== undefined && body !== null) {
            options.body = JSON.stringify(body)
            options.headers["Content-Type"] = "application/json"
        }
        return fetchWithRetry(url.toString(), options, GATEWAY_STATUSES).then(handleResponse)
    }

    const getEnrich = (path, query) => request(path, {query})
    const postEnrich = (path, body) => request(path, {method: "POST", body})

    const rejectWithoutHref = (name, hallink) => {
        const error = new Error(
            `[${name}] HAL-Link ohne href — der Aufruf wurde nicht abgeschickt. ` +
                `Gelesen wurde: ${JSON.stringify(hallink) ?? "undefined"}`,
        )
        console.error(error)
        return Promise.reject(error)
    }

    const callBackend = (hallink, {body, query} = {}) => {
        if (!hallink?.href) return rejectWithoutHref("callBackend", hallink)
        // Nur Netzwerkabbrüche wiederholen — der Aufruf kann eine echte Mutation am
        // Fach-Backend sein, deshalb hier bewusst kein Retry auf 502/503/504.
        return fetchWithRetry(enrichBaseUrl() + "/call", {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                link: {href: hallink.href, methods: hallink.methods ?? ["GET"]},
                ...(body !== undefined && body !== null ? {body} : {}),
                ...(query ? {query} : {}),
            }),
        }).then(handleBackendResponse)
    }

    const callHallink = (hallink, body) => callBackend(hallink, {body})

    /**
     * Binärpfad über den tom-server: PDF/Datei rein oder raus. Gibt die rohe Response
     * zurück, damit der Aufrufer `blob()` bzw. den Status selbst auswerten kann.
     *
     * @param hallink  {href, methods}
     * @param options  {body, contentType, accept}
     */
    const callBackendFile = (hallink, {body, contentType, accept} = {}) => {
        if (!hallink?.href) return rejectWithoutHref("callBackendFile", hallink)
        const url = new URL(enrichBaseUrl() + "/file")
        url.searchParams.set("href", hallink.href)
        url.searchParams.set(
            "method",
            hallink?.methods?.[0] ?? (body != null ? "POST" : "GET"),
        )
        const headers = {Authorization: `Bearer ${token}`}
        if (contentType) headers["Content-Type"] = contentType
        if (accept) headers.Accept = accept
        return fetch(url.toString(), {method: "POST", headers, body}).then((response) => {
            if (response.status === 401) {
                triggerReAuth()
                return Promise.reject(response)
            }
            return response
        })
    }

    return {
        setToken,
        setReAuthHandler,
        getEnrich,
        postEnrich,
        callBackend,
        callHallink,
        callBackendFile,
    }
}

const TomClient = TomClientGen()

export default TomClient
